<template>
  <div>
    <div
      class="bg-white border border-untitled-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 p-4 sm:p-5"
    >
      <V4NoteEntry v-if="!isOpenAnotheNotes" :note="firstNote">
        <template #meta>
          <span class="text-xs text-untitled-gray-400">
            {{ firstNote?.note_replies?.length ?? 0 }}
            {{ firstNote?.note_replies?.length === 1 ? "Reply" : "Replies" }}
          </span>
        </template>
        <template #actions>
          <V4NoteActions
            :replies-count="firstNote?.note_replies?.length ?? 0"
            :is-open="isOpen"
            :is-owner="firstNote?.author?.id === user?.id"
            @view="gotoUnit(firstNote?.unit)"
            @toggle="isOpen = !isOpen"
            @edit="editNote(firstNote)"
            @delete="deleteNote(firstNote)"
          />
        </template>
      </V4NoteEntry>

      <div
        v-if="isOpen && !isOpenAnotheNotes"
        class="flex flex-col gap-4 mt-4 pt-4 border-t border-untitled-gray-100"
      >
        <div
          v-for="reply in firstNote?.note_replies"
          :key="reply?.id"
          class="pl-4 border-l-2 border-untitled-gray-100"
        >
          <V4NoteEntry :note="reply" compact />
        </div>
      </div>

      <template v-if="isOpenAnotheNotes">
        <div class="flex flex-col gap-4">
          <v4-sub-note-card
            v-for="anotherNotes in groupedNote"
            :key="anotherNotes?.id"
            :note="anotherNotes"
          ></v4-sub-note-card>
        </div>
      </template>

      <div v-if="groupedNote.length > 1" class="flex justify-center mt-4">
        <button
          type="button"
          @click="isOpenAnotheNotes = !isOpenAnotheNotes"
          class="inline-flex items-center gap-1 text-xs font-medium text-untitled-gray-500 hover:text-app-primary transition-colors"
        >
          {{ isOpenAnotheNotes ? "Hide" : "See" }} more comments
          <ChevronDownIcon
            class="w-4 h-4 transition-transform"
            :class="isOpenAnotheNotes ? 'rotate-180' : ''"
          />
        </button>
      </div>
    </div>

    <el-dialog v-model="editNoteDialog" width="90%" class="!max-w-md" :show-close="false">
      <template #header>
        <h4 class="font-bold text-untitled-gray-800">Edit Comment</h4>
      </template>
      <div class="flex flex-col gap-4 px-1 py-2">
        <v-textarea
          v-model="currentNote.value"
          placeholder="Comment"
          maxlength="2000"
        ></v-textarea>
        <div class="flex justify-end items-center gap-2">
          <v-button @click="editNoteDialog = false" :disabled="loading"
            >Cancel</v-button
          >
          <v-button
            :disabled="!currentNote?.value.length"
            @click="saveNoteAPI"
            :loading="loading"
            >Save</v-button
          >
        </div>
      </div>
    </el-dialog>

    <ConfirmDialog
      :open="confirmDialog"
      :is-custom="true"
      id="V4MyNote-ConfirmDialog-Dialogbox"
    >
      <template #custom_html>
        <div class="text-sm text-gray-500">
          <p class="">
            This comment will be deleted. Are you sure you want to delete?
          </p>
        </div>
      </template>
      <template #action_button>
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            class="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm text-untitled-gray-800 bg-untitled-gray-200 hover:bg-untitled-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-untitled-gray-500 sm:ml-3 sm:w-auto"
            @click="onClickConfirmOk"
          >
            OK
          </button>
          <button
            type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm hover:bg-untitled-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-untitled-gray-500 sm:mt-0 sm:w-auto"
            @click="confirmDialog = false"
            ref="cancelButtonRef"
          >
            Cancel
          </button>
        </div>
      </template>
    </ConfirmDialog>
  </div>
</template>

<script setup>
import { ref, defineProps, inject, computed } from "vue";
import { VButton, VTextarea } from "revdojo-vue-components";
import ConfirmDialog from "@/components/dialogs/ConfirmDialog";
import { useRouter } from "vue-router";
import Bugsnag from "@bugsnag/js";
import eventBus from "@/plugins/eventBus";
import { useAuthStore } from "@/stores";
import { ElDialog } from "element-plus";
import { notificationMessage } from "@/helpers/error_message";
import V4SubNoteCard from "./V4SubNoteCard.vue";
import V4NoteEntry from "./V4NoteEntry.vue";
import V4NoteActions from "./V4NoteActions.vue";
import { ChevronDownIcon } from "@heroicons/vue/solid";
import { useDialogStore } from "@/stores/useDialogStore";

const dialogStore = useDialogStore();

const props = defineProps({
  groupedNote: {
    default: null,
    type: Object,
  },
});

const axios = inject("axios");
const router = useRouter();
const authUser = useAuthStore();
const isOpen = ref(false);
const isOpenAnotheNotes = ref(false);
const editNoteDialog = ref(false);
const currentNote = ref(null);
const confirmDialog = ref(false);
const loading = ref(false);

const gotoUnit = (unit) => {
  if (unit.type === 'scorm') {
    return router.push({
      name: "ScormCourseWatch",
      params: {
        id: unit.scorm_provider_id,
      },
    })
  }

  router.push(`/units/${unit.id}`);
};

const deleteNoteAPI = (id) => {
  axios
    .delete(`/api/v3/notes/${id}`)
    .then(() => {
      eventBus.$emit("DELETE_NOTE", id);

      eventBus.$emit(
        "CREATE_NOTIFICATION",
        "Comment deleted successfully.",
        "success",
        6500
      );

      confirmDialog.value = false;
    })
    .catch((error) => {
      Bugsnag.notify(error);
      if (error.response?.status === 500) {
        eventBus.$emit(
          "CREATE_NOTIFICATION",
          "Comment deleted successfully.",
          "success",
          6500
        );
      }
    });
};

const saveNoteAPI = () => {
  loading.value = true;

  axios
    .put(`/api/v3/notes`, {
      id: currentNote.value?.id,
      value: currentNote.value?.value,
    })
    .then(({ data }) => {
      loading.value = false;
      eventBus.$emit("UPDATE_NOTE", data);

      eventBus.$emit(
        "CREATE_NOTIFICATION",
        "Saved successfully.",
        "success",
        6500
      );
      editNoteDialog.value = false;
    })
    .catch((error) => {
      Bugsnag.notify(error);
      loading.value = false;
      if (error.response?.status === 500) {
        eventBus.$emit(
          "CREATE_NOTIFICATION",
          notificationMessage[500],
          "server_error",
          6500
        );
      }
    });
};

const editNote = (note) => {
  currentNote.value = { ...note };
  editNoteDialog.value = true;
  dialogStore.isDialogVisible = true
};

const deleteNote = (note) => {
  currentNote.value = { ...note };
  confirmDialog.value = true;
};

const onClickConfirmOk = () => {
  deleteNoteAPI(currentNote?.value.id);
};

const user = computed(() => {
  return authUser.$state.user;
});

const firstNote = computed(() => {
  if (!props.groupedNote.length) {
    return null;
  }

  return props.groupedNote[0];
});
</script>
